import React, { FC, useEffect, useState } from 'react';
import cls from './NotificationAlert.module.scss';
import {
  Notification,
  NOTIFICATIONS_TYPES_ENUM,
} from 'src/models/notifications';
import useSounds from 'src/hooks/useSounds';
import {
  usePostNotificationDoneMutation,
  usePostNotificationExtendMutation,
} from 'src/services/notification';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpdateNotificationEventMutation } from 'src/services/pytonSSE';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import { toast } from 'react-toastify';
import { useTypedDispatch } from 'src/hooks/useTypedDispatch';
import { onNotificationDone } from 'src/redux/slices/notificationsSlice';
import CloseModalIcon from 'src/assets/icons/mathSigns/CloseModalIcon';
import BellIcon from 'src/assets/icons/tools/BellIcon';
import LongListIcon from 'src/assets/icons/lists/LongListIcon';
import LinkIcon from '../../../../../assets/icons/mathSigns/LinkIcon';
import Tooltip from '../../../../../components/UI/Tooltip/Tooltip';

interface AlertProps {
  notification: Notification | null;
  onClose: () => void;
}

const NotificationAlert: FC<AlertProps> = ({ notification, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'notification-alert',
  });
  const timeList = [
    { title: t('5-min'), value: 5 },
    { title: t('10-min'), value: 10 },
    { title: t('15-min'), value: 15 },
    { title: t('30-min'), value: 30 },
    { title: t('1-hour'), value: 60 },
  ];
  const { playAlertSound } = useSounds();
  const [extend] = usePostNotificationExtendMutation();
  const [done] = usePostNotificationDoneMutation();
  const [updateEvent] = useUpdateNotificationEventMutation();
  const [time, setTime] = useState<string>('5');
  const userLogin = useTypedSelector(selectors.getUserName);

  const onExtend = async () => {
    await extend({ id: notification?.id, minutes: time, sent: false })
      .unwrap()
      .then(() =>
        updateEvent({
          notification_id: notification?.id,
          newUserAgentLogin: userLogin,
        })
      );
    return onClose();
  };
  const dispatch = useTypedDispatch();

  const onDone = async () => {
    await done({ id: notification?.id })
      .unwrap()
      .then(() => dispatch(onNotificationDone(notification?.id)))
      .catch((e) => toast.error(e?.data?.message || 'error'));
    return onClose();
  };

  const onRedirectToOrderClick = () => {
    navigate({ pathname: `/orders/${notification?.order_id}` });
  };

  const onCloseNotification = async () => {
    await extend({ id: notification?.id, minutes: 1, sent: false })
      .unwrap()
      .then(() =>
        updateEvent({
          notification_id: notification?.id,
          newUserAgentLogin: userLogin,
        })
      );

    return onClose();
  };

  useEffect(() => {
    playAlertSound();
  }, []);

  return (
    <div className={cls.alert}>
      <div className="flex-between">
        <h4>
          {t('notification')}: {notification?.id}
        </h4>
        <div className="flex-center gap-5">
          {notification?.order_id ? (
            <Tooltip title={t('link-text', { orderId: notification.order_id })}>
              <LinkIcon
                className="cursor-pointer"
                onClick={onRedirectToOrderClick}
              />
            </Tooltip>
          ) : null}
          <CloseModalIcon
            className="cursor-pointer"
            onClick={onCloseNotification}
          />
        </div>
      </div>
      <div className={cls.card}>
        <div className="align-center">
          <BellIcon className={cls.icon} />
          <div className={cls.text}>
            {notification?.type === NOTIFICATIONS_TYPES_ENUM.NEW_ORDER_RECALL
              ? t('recall')
              : t('ordinary')}
          </div>
        </div>
        <div className="align-center mt-10">
          <LongListIcon className={cls.icon} />
          {notification?.description && (
            <div className={cls.text}>{notification.description}</div>
          )}
        </div>
      </div>
      <div className={`align-center ${cls.move}`}>
        <button onClick={onExtend}>{t('move-to')}</button>
        <select
          onChange={(e) => setTime(e.target.value)}
          className={cls.select}
        >
          {timeList.map((i) => (
            <option key={i.value} value={i.value}>
              {i.title}
            </option>
          ))}
        </select>
      </div>
      <div className={`align-center ${cls.submit}`}>
        <button onClick={onDone} className="submit-button m-0">
          {t('completed')}
        </button>
        <span className={cls.description}>{notification?.date_time}</span>
      </div>
    </div>
  );
};

export default NotificationAlert;
